import React, { useEffect, useRef, useState } from 'react';
import { CrudList, CrudStyles } from '../shared/Crud';
import MessageModal from '../shared/MessageModal';
import costCenterService from '../../services/costCenterService';
import { COST_CENTER_EMPLOYEE_LIST_COLUMNS } from './costCenterEmployeeListColumns';

const { FormGroup, Input, Button } = CrudStyles;

const FILTER_DEBOUNCE_MS = 500;

const rowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'flex-end',
    width: '100%',
};

const fieldStyle = { flex: '1 1 180px', minWidth: 0 };

export default function CostCenterEligibleEmployeesList({
    customerId,
    costCenterId,
    onAssociated,
    idPrefix = 'cc-elig-emp',
    /** Dentro de modal o título já está no cabeçalho da janela. */
    embedInModal = false,
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [localName, setLocalName] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');

    const [associatingId, setAssociatingId] = useState(null);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [reloadTick, setReloadTick] = useState(0);

    const isInitialMount = useRef(true);

    useEffect(() => {
        setLocalName('');
        setLocalEmail('');
        setSearchName('');
        setSearchEmail('');
        setPagination({ page: 0, totalPages: 0, first: true, last: true });
        setLoadError(null);
        setAssociatingId(null);
        isInitialMount.current = true;
    }, [customerId, costCenterId]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const t = setTimeout(() => {
            setSearchName(localName);
            setSearchEmail(localEmail);
            setPagination((p) => ({ ...p, page: 0 }));
        }, FILTER_DEBOUNCE_MS);

        return () => clearTimeout(t);
    }, [localName, localEmail]);

    useEffect(() => {
        if (customerId == null || customerId === '' || costCenterId == null || costCenterId === '') {
            setData([]);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const result = await costCenterService.listEligibleEmployees(customerId, costCenterId, {
                    page: pagination.page,
                    size: 10,
                    sort: 'name,asc',
                    ...(searchName.trim() ? { name: searchName.trim() } : {}),
                    ...(searchEmail.trim() ? { email: searchEmail.trim() } : {}),
                });

                if (cancelled) return;

                const content = result.content || [];
                const apiTotal =
                    result.page?.totalPages != null && result.page.totalPages !== ''
                        ? Number(result.page.totalPages)
                        : null;
                const totalPages =
                    apiTotal != null && Number.isFinite(apiTotal)
                        ? Math.max(0, apiTotal)
                        : content.length === 0
                          ? 0
                          : 1;
                const rawNumber = result.page?.number ?? 0;
                const pageNumber =
                    totalPages === 0 ? 0 : Math.min(Number(rawNumber) || 0, Math.max(0, totalPages - 1));

                setData(content);
                setPagination((prev) => ({
                    ...prev,
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                }));
            } catch {
                if (!cancelled) {
                    setLoadError('Não foi possível carregar os colaboradores elegíveis.');
                    setData([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [customerId, costCenterId, pagination.page, searchName, searchEmail, reloadTick]);

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleClearFilters = () => {
        setLocalName('');
        setLocalEmail('');
        setSearchName('');
        setSearchEmail('');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const hasTextFilters = Boolean(localName.trim() || localEmail.trim());

    const handleAssociate = async (employee) => {
        if (!employee?.id) return;
        const ok = window.confirm('Adicionar este colaborador ao centro de custo?');
        if (!ok) return;

        try {
            setAssociatingId(employee.id);
            await costCenterService.insertEmployee(customerId, costCenterId, employee.id);
            onAssociated?.();
        } catch (e) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: 'Erro ao associar colaborador ao centro de custo.',
            });
        } finally {
            setAssociatingId(null);
        }
    };

    const renderActions = (item) => (
        <Button
            variant="primary"
            type="button"
            onClick={() => handleAssociate(item)}
            disabled={associatingId === item?.id}
            title={associatingId === item?.id ? 'Associando...' : 'Adicionar ao centro de custo'}
        >
            {associatingId === item?.id ? 'Adicionando...' : 'Adicionar'}
        </Button>
    );

    return (
        <>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
            <CrudList
            title="Colaboradores elegíveis"
            hideHeader={embedInModal}
            tableMinWidth="620px"
            columns={COST_CENTER_EMPLOYEE_LIST_COLUMNS}
            data={data}
            loading={loading}
            actions={renderActions}
            pagination={{
                ...pagination,
                onPageChange: handlePageChange,
            }}
            filters={
                <>
                    <div style={rowStyle}>
                        <FormGroup style={fieldStyle}>
                            <label htmlFor={`${idPrefix}-name`}>Nome</label>
                            <Input
                                id={`${idPrefix}-name`}
                                type="text"
                                value={localName}
                                onChange={(e) => setLocalName(e.target.value)}
                                placeholder="Filtrar por nome"
                                autoComplete="off"
                            />
                        </FormGroup>
                        <FormGroup style={fieldStyle}>
                            <label htmlFor={`${idPrefix}-email`}>E-mail</label>
                            <Input
                                id={`${idPrefix}-email`}
                                type="text"
                                value={localEmail}
                                onChange={(e) => setLocalEmail(e.target.value)}
                                placeholder="Filtrar por e-mail"
                                autoComplete="off"
                            />
                        </FormGroup>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClearFilters}
                            disabled={!hasTextFilters}
                            title={hasTextFilters ? 'Limpar filtros de texto' : 'Nenhum filtro de texto aplicado'}
                        >
                            Limpar
                        </Button>
                    </div>
                    {loadError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} role="alert">
                            <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                            <button
                                type="button"
                                onClick={() => setReloadTick((v) => v + 1)}
                                style={{
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    borderRadius: 4,
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    )}
                </>
            }
        />
        </>
    );
}

